/**
 * Hook pour gérer l'impersonation (SuperAdmin)
 * Utilise impersonationAPI depuis superadmin.service
 *
 * ARCHITECTURE: Ce hook ne fait PAS d'appel API automatique.
 * L'appelant doit explicitement appeler checkActiveImpersonation()
 * après avoir vérifié que l'utilisateur est SUPERADMIN.
 */

import { useState, useCallback } from 'react';
import { notifications } from '@/lib/notifications';
import { impersonationAPI, type ImpersonationSession } from '../services/superadmin.service';
import { setAccessToken } from '@/services/tokenManager';

export function useImpersonation() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeSession, setActiveSession] = useState<ImpersonationSession | null>(null);
  const [isChecked, setIsChecked] = useState(false);

  /**
   * Vérifier s'il y a une impersonation active
   * DOIT être appelé explicitement par le composant après vérification du rôle
   */
  const checkActiveImpersonation = useCallback(async (): Promise<ImpersonationSession | null> => {
    if (isChecked) return activeSession;

    setIsLoading(true);
    try {
      const session = await impersonationAPI.checkActive();
      setActiveSession(session);
      setIsChecked(true);
      return session;
    } catch {
      setActiveSession(null);
      setIsChecked(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isChecked, activeSession]);

  /**
   * Démarrer l'impersonation d'un utilisateur
   */
  const startImpersonation = useCallback(async (userId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const data = await impersonationAPI.start(userId);

      if (data.accessToken) {
        // Default expiration of 24 hours (86400 seconds) for impersonation tokens
        setAccessToken(data.accessToken, data.expiresIn || 86400);
      }

      setActiveSession(data);
      notifications.success('Mode impersonation activé', 'Succès');

      setTimeout(() => {
        window.location.href = '/';
      }, 500);

      return true;
    } catch (error: any) {
      notifications.error(error.message || "Erreur lors du démarrage de l'impersonation", 'Erreur');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Arrêter l'impersonation en cours
   */
  const stopImpersonation = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const data = await impersonationAPI.stop();

      if (data.accessToken) {
        // Default expiration of 24 hours (86400 seconds) for restored tokens
        setAccessToken(data.accessToken, data.expiresIn || 86400);
      }

      setActiveSession(null);
      notifications.success('Mode impersonation désactivé', 'Succès');

      setTimeout(() => {
        window.location.href = '/superadmin/users';
      }, 500);

      return true;
    } catch (error: any) {
      notifications.error(error.message || "Erreur lors de l'arrêt de l'impersonation", 'Erreur');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Récupérer l'historique des impersonations
   */
  const getImpersonationHistory = useCallback(async (params?: {
    page?: number;
    limit?: number;
    userId?: string;
    superadminId?: string;
  }): Promise<any> => {
    setIsLoading(true);
    try {
      const data = await impersonationAPI.getHistory(params);
      return data;
    } catch (error: any) {
      notifications.error(error.message || "Erreur lors de la récupération de l'historique", 'Erreur');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    isChecked,
    activeSession,
    startImpersonation,
    stopImpersonation,
    checkActiveImpersonation,
    getImpersonationHistory,
  };
}

// Re-export types
export type { ImpersonationSession } from '../services/superadmin.service';
